// OL side of the sidebar entry: features in, entry DOM out, entries registered so the
// other rows, the hover and the drawer can find them. The pure modules do the work.
import { curvatureAtJunctionKeyValues } from "../layers/style/curvature";
import { sourceWkt } from "../util/featureWkt.mjs";
import { buildCajView, relationIdForArc } from "./cajView.mjs";
import { plainTextOf, trimmedBounds } from "./copyText.mjs";
import { createEntryRegistry } from "./entryRegistry.mjs";
import { buildFeatureModel } from "./featureModel.mjs";
import { buildLaneView } from "./laneView.mjs";
import { applyCursor, clearCursor } from "./cursorDom.mjs";
import { cursorTargets } from "./liveCursor.mjs";
import { buildLmRestrictionView } from "./lmRestrictionView.mjs";
import {
  clearMemberHover,
  clearPositionHover,
  lookupMember,
  showMemberHover,
  showPositionHover,
} from "./memberHover.js";
import { installPointerTip } from "./pointerTip.js";
import { renderFeatureEntry } from "./renderEntry.mjs";
import { renderCajView, renderLaneView, renderLmView } from "./renderViews.mjs";
import {
  RANGE_SELECTOR,
  TRACK_SELECTOR,
  rangeOf,
  trackPosition,
} from "./trackHover.mjs";

export const entryRegistry = createEntryRegistry();

const entriesByControl = new WeakMap();
let clickCounter = 0;
let stampOpen = false;
// One stamp per click: a map click selects in several rows within one task.
function clickStamp() {
  if (!stampOpen) {
    clickCounter++;
    stampOpen = true;
    queueMicrotask(() => (stampOpen = false));
  }
  return clickCounter;
}

function modelOfFeature(feature) {
  const length = feature.get("lengthCm");
  return buildFeatureModel({
    id: String(feature.getId() ?? ""),
    properties: feature.getProperties(),
    geometryType: feature.getGeometry()?.getType(),
    lengthCm: Number.isFinite(length) ? length : null,
  });
}

// A CaJ arc is our drawing of a relation: show the relation. The arc's own properties
// (type, relation-id, its copy of curvature_at_junction) are drawing bookkeeping.
function arcRelation(feature, source) {
  const relationId = relationIdForArc(feature.getProperties());
  return relationId && source ? source.getFeatureById(relationId) : null;
}

function viewsOf(model) {
  const views = [];
  const used = [];
  const lanes = buildLaneView(model);
  if (lanes) {
    views.push(renderLaneView(document, lanes));
    used.push(...lanes.usedTags);
  }
  const lm = buildLmRestrictionView(model);
  if (lm) {
    views.push(renderLmView(document, lm));
    used.push(...lm.usedTags);
  }
  const caj = buildCajView(model);
  if (caj) {
    const derived = curvatureAtJunctionKeyValues(caj.value).slice(1);
    views.push(renderCajView(document, caj, derived));
    used.push(...caj.usedTags);
  }
  return {
    views,
    viewTagKeys: new Set(used.map((t) => t.rawKey)),
    laneView: lanes,
  };
}

const entriesListeners = [];

/** Called by the drawer to follow the last clicked entry. */
export function onEntriesChanged(listener) {
  entriesListeners.push(listener);
}

function refreshAlsoLines() {
  entryRegistry.entries().forEach((entry) => {
    const row = entry.element.querySelector(".fe-also");
    if (!row) return;
    const rows = entryRegistry.alsoSelectedIn(entry.featureId, entry.rowName);
    row.style.display = rows.length ? "" : "none";
    row.querySelector(".fe-alsorows").textContent = rows.join(", ");
  });
  entriesListeners.forEach((listener) => listener());
}

/** Forget a row's entries (its selection changed or was cleared). */
export function releaseEntries(control) {
  (entriesByControl.get(control) ?? []).forEach((e) => entryRegistry.remove(e));
  entriesByControl.delete(control);
  refreshAlsoLines();
}

/**
 * @param {import("../controls/VectorFeatureControl").VectorFeatureControl} control
 * @param {import("ol/Feature").default[]} features in sidebar order
 * @param {{rowName: string, headerField?: string, source?: import("ol/source/Vector").default}} options
 * @returns {HTMLElement[]} one `tbody.fe-entry` per feature
 */
export function renderSelectedFeatures(control, features, options) {
  const { rowName, headerField, source } = options;
  (entriesByControl.get(control) ?? []).forEach((e) => entryRegistry.remove(e));
  const clickedAt = clickStamp();
  const created = features.map((feature) => {
    const relation = arcRelation(feature, source);
    const shown = relation ?? feature;
    const model = modelOfFeature(shown);
    const { views, viewTagKeys, laneView } = viewsOf(model);
    const missingMemberIds = new Set(
      model.members.filter((m) => !lookupMember(m.id, source)).map((m) => m.id),
    );
    const element = renderFeatureEntry(document, model, {
      headerText: headerField ? String(shown.get(headerField)) : undefined,
      wkt: sourceWkt(shown),
      alsoIn: entryRegistry.alsoSelectedIn(model.id, rowName),
      views,
      viewTagKeys,
      missingMemberIds,
    });
    const entry = {
      rowName,
      featureId: model.id,
      memberIds: model.members.map((m) => m.id),
      clickedAt,
      element,
      model,
      laneView,
      feature: shown,
      source,
    };
    entryRegistry.add(entry);
    return entry;
  });
  entriesByControl.set(control, created);
  refreshAlsoLines();
  return created.map((e) => e.element);
}

/** Copy buttons, member hover and the pointer tooltip, once per attributes container. */
export function installEntryEvents(container) {
  installPointerTip(container);
  installTrackHover(container, (node) =>
    entryRegistry.entries().find((e) => e.element.contains(node)),
  );
  let hoveredRow = null;
  container.addEventListener("mouseover", (event) => {
    const row =
      event.target instanceof Element
        ? event.target.closest("tr[data-member], tr.fe-head")
        : null;
    if (row === hoveredRow) return;
    hoveredRow = row;
    const entry =
      row && entryRegistry.entries().find((e) => e.element.contains(row));
    if (!entry || entry.model.members.length === 0) {
      clearMemberHover();
      return;
    }
    showMemberHover(
      entry,
      row.classList.contains("fe-head") ? null : Number(row.dataset.index),
    );
  });
  container.addEventListener("mouseleave", () => {
    hoveredRow = null;
    clearMemberHover();
  });
  container.addEventListener("click", (event) => {
    const button =
      event.target instanceof Element && event.target.closest("[data-copy]");
    if (!button) return;
    navigator.clipboard?.writeText(button.dataset.copy).then(() => {
      button.classList.add("fe-copied");
      setTimeout(() => button.classList.remove("fe-copied"), 900);
    });
  });
  container.addEventListener("dblclick", selectWholeValue);
  copyContainers.add(container);
  if (copyContainers.size === 1)
    document.addEventListener("copy", copyEntrySelection);
}

// Double-click: the whole key, value, id or range under the pointer, no blank around.
const SELECT_UNIT =
  ".fe-id, .fe-key, .fe-item, .fe-value, .fe-range, .fe-slot, .fe-lsv, .fe-mono, .v, td, th";

function selectWholeValue(event) {
  const unit =
    event.target instanceof Element && event.target.closest(SELECT_UNIT);
  if (!unit || unit.closest("button, summary, textarea, input")) return;
  const walker = document.createTreeWalker(unit, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.parentElement?.closest("button, summary")
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });
  const texts = [];
  while (walker.nextNode()) texts.push(walker.currentNode);
  const bounds = trimmedBounds(texts.map((t) => t.data));
  if (!bounds) return;
  const range = document.createRange();
  range.setStart(texts[bounds.start[0]], bounds.start[1]);
  range.setEnd(texts[bounds.end[0]], bounds.end[1]);
  const selection = document.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
}

// Copy: a key and its value on one line, tab-separated; buttons and expand labels out.
const copyContainers = new Set();

function copyEntrySelection(event) {
  const selection = document.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);
  const inEntries = [...copyContainers].some((c) =>
    c.contains(range.commonAncestorContainer),
  );
  if (!inEntries) return;
  const text = plainTextOf(range.cloneContents());
  if (!text) return;
  event.clipboardData.setData("text/plain", text);
  event.preventDefault();
}

let cursorEntries = [];
const cursorListeners = [];

/** Called by the drawer to follow the hover position. */
export function onLiveCursor(listener) {
  cursorListeners.push(listener);
}

/**
 * @param {import("ol/Feature").default|null} feature the closest hovered line, or null
 * @param {number} [distanceCm] position along it
 */
export function moveLiveCursor(feature, distanceCm) {
  const entries = feature
    ? entryRegistry.entries().filter((e) => e.feature === feature)
    : [];
  cursorEntries
    .filter((e) => !entries.includes(e))
    .forEach((e) => clearCursor(e.element));
  cursorEntries = entries;
  entries.forEach((e) =>
    applyCursor(e.element, cursorTargets(e.model, e.laneView, distanceCm)),
  );
  cursorListeners.forEach((listener) => listener(feature, distanceCm));
}

/**
 * Bar and profile hover, sidebar → map: the position under the pointer moves the live
 * cursor (sidebar and drawer, as a map hover does) and is drawn on the map with the
 * range of the section or band under it. A lanes stretch row draws its range.
 * @param {Element} root
 * @param {(node: Element) => object|undefined} entryOf the entry a node belongs to
 */
export function installTrackHover(root, entryOf) {
  let active = false;
  const clear = () => {
    if (!active) return;
    active = false;
    moveLiveCursor(null);
    clearPositionHover();
  };
  root.addEventListener("mousemove", (event) => {
    // A drag is a text selection: nothing follows the pointer meanwhile.
    if (event.buttons) {
      clear();
      return;
    }
    const target = event.target instanceof Element ? event.target : null;
    const track = target?.closest(TRACK_SELECTOR);
    const stretch = !track && target?.closest(".fe-ls");
    const entry = (track || stretch) && entryOf(track || stretch);
    if (!entry) {
      clear();
      return;
    }
    active = true;
    if (stretch) {
      moveLiveCursor(null);
      showPositionHover(entry.feature, null, rangeOf(stretch.dataset));
      return;
    }
    const ranged = target.closest(RANGE_SELECTOR);
    const cm = trackPosition(
      track.getBoundingClientRect(),
      event.clientX,
      Number(track.dataset.axis),
    );
    moveLiveCursor(entry.feature, cm);
    showPositionHover(
      entry.feature,
      cm,
      ranged && track.contains(ranged) ? rangeOf(ranged.dataset) : null,
    );
  });
  root.addEventListener("mouseleave", clear);
}

// Member hover, map → sidebar: rows of open entries whose member is under the pointer.
let hotRows = [];
let hotKey = "";

/** @param {Set<string>} featureIds ids of the features under the pointer */
export function markMemberRows(featureIds) {
  const key = [...featureIds].sort().join("\n");
  if (key === hotKey) return;
  hotKey = key;
  hotRows.forEach((row) => row.classList.remove("fe-hot"));
  hotRows = [];
  if (featureIds.size === 0) return;
  entryRegistry.entries().forEach((entry) => {
    if (!entry.memberIds.some((id) => featureIds.has(id))) return;
    entry.element.querySelectorAll("tr[data-member]").forEach((row) => {
      if (!featureIds.has(row.dataset.member)) return;
      row.classList.add("fe-hot");
      hotRows.push(row);
    });
  });
}
